import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { rolePermissionsAPI } from '../services/api';
import { toast } from '../components/common/Toast';
import { Shield, RefreshCw, Save, Eye, Plus, Edit2, Trash2, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { errMsg } from '../utils/helpers';

const ALL_PATHS = [
  { path: '/',                 labelKey: 'dashboard' },
  { path: '/properties',       labelKey: 'properties' },
  { path: '/apartments',       labelKey: 'apartments' },
  { path: '/payments',         labelKey: 'payments' },
  { path: '/work-orders',      labelKey: 'workOrders' },
  { path: '/leases',           labelKey: 'leases' },
  { path: '/tenants',          labelKey: 'tenants' },
  { path: '/inventory',        labelKey: 'inventory' },
  { path: '/documents',        labelKey: 'documents' },
  { path: '/expenses',         labelKey: 'expenses' },
  { path: '/budget',           labelKey: 'budget' },
  { path: '/notifications',    labelKey: 'notifications' },
  { path: '/users',            labelKey: 'users' },
  { path: '/role-permissions', labelKey: 'rolePermissions' },
];

const MODULES = [
  { key: 'properties', labelKey: 'properties' },
  { key: 'apartments', labelKey: 'apartments' },
  { key: 'payments',   labelKey: 'payments' },
  { key: 'workOrders', labelKey: 'workOrders' },
  { key: 'leases',     labelKey: 'leases' },
  { key: 'tenants',    labelKey: 'tenants' },
  { key: 'inventory',  labelKey: 'inventory' },
  { key: 'documents',  labelKey: 'documents' },
  { key: 'expenses',   labelKey: 'expenses' },
  { key: 'budget',     labelKey: 'budget' },
  { key: 'users',      labelKey: 'users' },
];

const ACTIONS = [
  { key: 'view',   labelKey: 'view',   icon: Eye,    color: 'var(--brand)' },
  { key: 'create', labelKey: 'create', icon: Plus,   color: 'var(--emerald)' },
  { key: 'edit',   labelKey: 'edit',   icon: Edit2,  color: 'var(--amber)' },
  { key: 'delete', labelKey: 'delete', icon: Trash2, color: 'var(--rose)' },
];

const CONFIGURABLE_ROLES = ['manager', 'maintenance', 'accountant', 'contractor'];

const ROLE_COLORS = {
  manager:     'var(--brand)',
  maintenance: 'var(--amber)',
  accountant:  'var(--emerald)',
  contractor:  'var(--rose)',
};

export default function RolePermissions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [paths,   setPaths]   = useState({});   // { [role]: Set<string> }
  const [crud,    setCrud]    = useState({});    // { [role]: { [module]: { [action]: bool } } }
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState({});
  const [tab,     setTab]     = useState('crud'); // 'menu' | 'crud'

  const visibleRoles = isAdmin
    ? CONFIGURABLE_ROLES
    : CONFIGURABLE_ROLES.filter(r => r !== 'manager');

  const load = () => {
    setLoading(true);
    rolePermissionsAPI.getAll()
      .then(r => {
        const pMap = {}, cMap = {};
        r.data.data.forEach(p => {
          pMap[p.role] = new Set(p.paths || []);
          cMap[p.role] = p.crud || {};
        });
        setPaths(pMap);
        setCrud(cMap);
      })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // ── Nav path toggle ──
  const togglePath = (role, path) => {
    setPaths(prev => {
      const next = new Set(prev[role] || []);
      if (next.has(path)) next.delete(path); else next.add(path);
      return { ...prev, [role]: next };
    });
  };

  // ── CRUD toggle ──
  const toggleCrud = (role, module, action) => {
    setCrud(prev => {
      const roleCrud = { ...(prev[role] || {}) };
      const modPerm  = { ...(roleCrud[module] || { view: false, create: false, edit: false, delete: false }) };
      modPerm[action] = !modPerm[action];
      // Unchecking view also clears all others (no point having create/edit without view)
      if (action === 'view' && !modPerm.view) {
        modPerm.create = false; modPerm.edit = false; modPerm.delete = false;
      }
      // Checking any write action also sets view
      if ((action === 'create' || action === 'edit' || action === 'delete') && modPerm[action]) {
        modPerm.view = true;
      }
      roleCrud[module] = modPerm;
      return { ...prev, [role]: roleCrud };
    });
  };

  // ── Toggle entire row (all actions for a module) ──
  const toggleModuleAll = (role, module) => {
    setCrud(prev => {
      const roleCrud = { ...(prev[role] || {}) };
      const modPerm  = roleCrud[module] || {};
      const allOn = ACTIONS.every(a => modPerm[a.key]);
      const next = {};
      ACTIONS.forEach(a => { next[a.key] = !allOn; });
      roleCrud[module] = next;
      return { ...prev, [role]: roleCrud };
    });
  };

  // ── Toggle entire column (all modules for an action) ──
  const toggleActionAll = (role, action) => {
    setCrud(prev => {
      const roleCrud = { ...(prev[role] || {}) };
      const allOn = MODULES.every(m => roleCrud[m.key]?.[action]);
      MODULES.forEach(m => {
        roleCrud[m.key] = { ...(roleCrud[m.key] || {}), [action]: !allOn };
        // If enabling a write action, enable view too
        if (action !== 'view' && !allOn) roleCrud[m.key].view = true;
        // If disabling view, disable all
        if (action === 'view' && allOn) {
          roleCrud[m.key].create = false;
          roleCrud[m.key].edit   = false;
          roleCrud[m.key].delete = false;
        }
      });
      return { ...prev, [role]: roleCrud };
    });
  };

  const saveRole = async (role) => {
    setSaving(s => ({ ...s, [role]: true }));
    try {
      await rolePermissionsAPI.update(role, {
        paths: Array.from(paths[role] || []),
        crud:  crud[role] || {},
      });
      toast.success(t('permissionsSaved'));
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(s => ({ ...s, [role]: false })); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('rolePermissions')}</h1>
          <p className="page-subtitle">{t('configureRoles')}</p>
        </div>
        <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16} /></button>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--surface-2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'crud', icon: Shield, label: t('crudPermissions') },
          { key: 'menu', icon: Menu,   label: t('navAccess') },
        ].map(({ key, icon: Icon, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={tab === key ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            style={{ gap: 7 }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {visibleRoles.map(role => (
            <div key={role} className="card" style={{ padding: 0, overflow: 'hidden' }}>

              {/* Role header */}
              <div style={{ padding: '14px 20px', background: ROLE_COLORS[role] + '12', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Shield size={18} color={ROLE_COLORS[role]} />
                  <span style={{ fontWeight: 700, fontSize: 15, textTransform: 'capitalize', color: ROLE_COLORS[role] }}>
                    {t(role)}
                  </span>
                  {tab === 'menu' && (
                    <span style={{ fontSize: 12, color: 'var(--ink-40)' }}>
                      — {paths[role]?.size || 0} {t('menuAccess')}
                    </span>
                  )}
                  {tab === 'crud' && (
                    <span style={{ fontSize: 12, color: 'var(--ink-40)' }}>
                      — {MODULES.filter(m => ACTIONS.some(a => crud[role]?.[m.key]?.[a.key])).length} {t('module')}(s)
                    </span>
                  )}
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => saveRole(role)} disabled={saving[role]} style={{ gap: 6 }}>
                  <Save size={13} />
                  {saving[role] ? t('saving') : t('savePermissions')}
                </button>
              </div>

              {/* ── Menu visibility tab ── */}
              {tab === 'menu' && (
                <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                  {ALL_PATHS.map(({ path, labelKey }) => {
                    const checked = paths[role]?.has(path) ?? false;
                    return (
                      <label key={path} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, background: checked ? ROLE_COLORS[role] + '10' : 'var(--surface-2)', border: `1px solid ${checked ? ROLE_COLORS[role] + '40' : 'transparent'}`, transition: 'all .15s', userSelect: 'none' }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePath(role, path)}
                          style={{ accentColor: ROLE_COLORS[role], width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400 }}>{t(labelKey)}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* ── CRUD permissions tab ── */}
              {tab === 'crud' && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-2)' }}>
                        <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.05em', width: '30%' }}>
                          {t('module')}
                        </th>
                        {ACTIONS.map(({ key, labelKey, icon: Icon, color }) => {
                          const allOn = MODULES.every(m => crud[role]?.[m.key]?.[key]);
                          return (
                            <th key={key} style={{ padding: '10px 16px', textAlign: 'center', fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                              <button onClick={() => toggleActionAll(role, key)}
                                style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', background: allOn ? color + '15' : 'transparent', border: `1px solid ${allOn ? color + '40' : 'var(--border)'}`, borderRadius: 6, padding: '3px 8px', color, transition: 'all .15s', margin: '0 auto', fontWeight: 700, fontSize: 11 }}
                                title={t('selectAll')}>
                                <Icon size={12} /> {t(labelKey)}
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {MODULES.map((mod, i) => {
                        const modPerm = crud[role]?.[mod.key] || {};
                        const allOn   = ACTIONS.every(a => modPerm[a.key]);
                        const anyOn   = ACTIONS.some(a => modPerm[a.key]);
                        return (
                          <tr key={mod.key} style={{ borderBottom: i < MODULES.length - 1 ? '1px solid var(--border-subtle)' : 'none', background: anyOn ? 'transparent' : 'var(--surface-2)08' }}>
                            <td style={{ padding: '11px 20px' }}>
                              <button onClick={() => toggleModuleAll(role, mod.key)}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'none', border: 'none', padding: 0, textAlign: 'left', width: '100%' }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: anyOn ? ROLE_COLORS[role] : 'var(--ink-20)', flexShrink: 0 }} />
                                <span style={{ fontSize: 13, fontWeight: anyOn ? 600 : 400, color: anyOn ? 'var(--ink)' : 'var(--ink-40)' }}>
                                  {t(mod.labelKey)}
                                </span>
                                {!anyOn && <span style={{ fontSize: 10, color: 'var(--ink-20)', marginLeft: 4 }}>{t('noAccess')}</span>}
                              </button>
                            </td>
                            {ACTIONS.map(({ key, color }) => {
                              const checked = modPerm[key] ?? false;
                              return (
                                <td key={key} style={{ padding: '11px 16px', textAlign: 'center' }}>
                                  <input type="checkbox" checked={checked}
                                    onChange={() => toggleCrud(role, mod.key, key)}
                                    style={{ accentColor: color, width: 16, height: 16, cursor: 'pointer' }} />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
